import type { Perm } from "../access/api";

/**
 * The first Help content (decision H5.1: start in the app; a staff page comes later). Only ids, rules and targets live
 * here; every word is in the locale files under `next.help.topics.<id>` and `next.help.news.<id>`, in en, he and fr.
 */
export type Tone = "blue" | "teal" | "amber" | "violet";
export type IconKey = "people" | "person" | "route" | "area" | "report" | "approve" | "combine" | "team" | "swap" | "download" | "map" | "heart" | "tour" | "sparkle";

export type TopicVideo = {
  url: string;
  /** Subtitle files by page language (WebVTT). A missing language falls back to English; none = the page says nothing about subtitles. */
  tracks?: Partial<Record<"en" | "he" | "fr", string>>;
  /** Length shown before play, "2:10". */
  length?: string;
};

export type Topic = {
  id: string;
  icon: IconKey;
  tone: Tone;
  minutes: number;
  /** The person must hold at least one of these switches. */
  needs?: Perm[];
  /** Hidden when the person holds any of these (the topic is for people who do NOT plan routes, for example). */
  hiddenWith?: Perm[];
  /** Also needs a team (a personal account has none). */
  needsTeam?: boolean;
  /** Only the people who help as volunteers see it, and they see only this. */
  volunteerOnly?: boolean;
  /** A label for the Owner and Managers ("For managers"); never the test. */
  managers?: boolean;
  /** Where "Take me there" opens, and the name of that page (`next.help.targets.<target>`). */
  to?: string;
  target?: "contacts" | "map" | "routes" | "reports" | "team";
  /** How many written steps (strings `steps.1` … `steps.N`; `steps.N_note` is an optional second line). */
  steps: number;
  /** Steps that have a second line (`steps.N_note`). */
  notes?: number[];
  /** Date of the last change, "2026-10-12". */
  updated: string;
  video?: TopicVideo;
};

export const TUTORIAL_VIDEO_URL = "https://connectyidfile.nyc3.cdn.digitaloceanspaces.com/Tutorial%20Video.mp4";

/** "The whole tour": today's getting-started film, kept as it is (decision H3.1). Own page, own id. */
export const WHOLE_TOUR_ID = "whole-tour";
export const WHOLE_TOUR: Topic = {
  id: WHOLE_TOUR_ID, icon: "tour", tone: "blue", minutes: 5, needs: ["contacts.read", "routes.read", "reports.write"], steps: 0, updated: "2026-09-01",
  video: { url: TUTORIAL_VIDEO_URL, length: "5:21" },
};

/** In the order shown. Twelve topics (fewer than 15, so no search box). */
export const TOPICS: Topic[] = [
  { id: "add-people", icon: "people", tone: "blue", minutes: 2, needs: ["contacts.add", "contacts.import"], to: "/households", target: "contacts", steps: 4, notes: [1], updated: "2026-10-12" },
  { id: "contact-page", icon: "person", tone: "teal", minutes: 2, needs: ["contacts.read"], to: "/households", target: "contacts", steps: 4, notes: [1], updated: "2026-10-12" },
  { id: "plan-route", icon: "route", tone: "amber", minutes: 2, needs: ["routes.plan"], to: "/map", target: "map", steps: 5, notes: [1], updated: "2026-10-12" },
  { id: "split-area", icon: "area", tone: "violet", minutes: 2, needs: ["routes.plan"], to: "/saved-routes", target: "routes", steps: 4, updated: "2026-10-12" },
  { id: "report-visit", icon: "report", tone: "blue", minutes: 1, needs: ["reports.write"], to: "/duchot?write=1", target: "reports", steps: 4, updated: "2026-10-12" },
  { id: "approve-changes", icon: "approve", tone: "teal", minutes: 2, needs: ["contacts.approve"], managers: true, steps: 3, updated: "2026-10-12" },
  { id: "combine-duplicates", icon: "combine", tone: "amber", minutes: 2, needs: ["contacts.combine"], managers: true, to: "/households", target: "contacts", steps: 4, updated: "2026-10-12" },
  { id: "add-team-member", icon: "team", tone: "violet", minutes: 2, needs: ["team.invite"], needsTeam: true, managers: true, to: "/my-team", target: "team", steps: 4, updated: "2026-10-27" },
  { id: "switch-accounts", icon: "swap", tone: "blue", minutes: 1, needs: ["contacts.read", "routes.read", "reports.write"], steps: 3, updated: "2026-10-12" },
  { id: "download-data", icon: "download", tone: "teal", minutes: 1, needs: ["contacts.export"], managers: true, to: "/households", target: "contacts", steps: 3, updated: "2026-10-12" },
  { id: "find-my-route", icon: "map", tone: "amber", minutes: 1, needs: ["routes.follow"], hiddenWith: ["routes.plan"], to: "/saved-routes", target: "routes", steps: 3, updated: "2026-10-12" },
  { id: "how-helping-works", icon: "heart", tone: "violet", minutes: 1, volunteerOnly: true, steps: 3, updated: "2026-10-12" },
];

export type NewsItem = {
  id: string;
  /** "2026-10-21" */
  date: string;
  icon: IconKey;
  tone: Tone;
  /** Who it fits: at least one of these switches. Owner and Managers see every item. */
  needs?: Perm[];
  /** Fits people who help as volunteers. */
  volunteer?: boolean;
  /** The "For managers" label. */
  managers?: boolean;
  /** Show me opens this address (the page shows the item's `hint` once). */
  to: string;
};

/**
 * "What's new in the new ConnectYid" (H4.1: the seven old write-ups are gone). A starting list; the real wording and
 * pictures come with the later content task.
 */
export const NEWS: NewsItem[] = [
  { id: "contacts-one-list", date: "2026-10-21", icon: "people", tone: "blue", needs: ["contacts.read"], to: "/households" },
  { id: "team-roles", date: "2026-10-14", icon: "team", tone: "violet", needs: ["team.invite"], managers: true, to: "/my-team" },
  { id: "reports-one-list", date: "2026-10-06", icon: "report", tone: "teal", needs: ["contacts.see_history", "reports.write"], to: "/duchot" },
  { id: "import-keeps-going", date: "2026-04-02", icon: "download", tone: "amber", needs: ["contacts.import"], to: "/households" },
  { id: "route-circle", date: "2026-02-19", icon: "route", tone: "amber", needs: ["routes.read"], to: "/saved-routes" },
  { id: "hebrew-subtitles", date: "2026-01-08", icon: "tour", tone: "blue", needs: ["contacts.read", "routes.read", "reports.write"], to: "/help/getting-started" },
];

export const helpTopicPath = (id: string) => `/help/getting-started/${id}`;
export const HELP_PATHS = { home: "/help", start: "/help/getting-started", news: "/help/whats-new" } as const;
